/**
 * Repair mojibake (UTF-8 bytes misread as Windows-1252/CP1252).
 *
 * Common in academic databases where Arabic, Chinese, or other non-Latin
 * titles are stored with incorrect encoding. Characters like Ù, Ø, Ã, …
 * appear when UTF-8 multi-byte sequences are decoded as CP1252 single chars.
 */

const MOJIBAKE_PATTERN = /[ÙØÃÂ]/

// Windows-1252 chars in the 0x80-0x9F range that differ from Latin-1
const CP1252_TO_BYTE: Record<number, number> = {
  0x20AC: 0x80, // €
  0x201A: 0x82, // ‚
  0x0192: 0x83, // ƒ
  0x201E: 0x84, // „
  0x2026: 0x85, // …
  0x2020: 0x86, // †
  0x2021: 0x87, // ‡
  0x02C6: 0x88, // ˆ
  0x2030: 0x89, // ‰
  0x0160: 0x8A, // Š
  0x2039: 0x8B, // ‹
  0x0152: 0x8C, // Œ
  0x017D: 0x8E, // Ž
  0x2018: 0x91, // '
  0x2019: 0x92, // '
  0x201C: 0x93, // "
  0x201D: 0x94, // "
  0x2022: 0x95, // •
  0x2013: 0x96, // –
  0x2014: 0x97, // —
  0x02DC: 0x98, // ˜
  0x2122: 0x99, // ™
  0x0161: 0x9A, // š
  0x203A: 0x9B, // ›
  0x0153: 0x9C, // œ
  0x017E: 0x9E, // ž
  0x0178: 0x9F, // Ÿ
}

/** Try to repair a mojibake string by re-encoding CP1252 → bytes → UTF-8. */
export function repairMojibake(text: string): string {
  if (!text || !MOJIBAKE_PATTERN.test(text)) return text

  try {
    const bytes: number[] = []
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i)
      if (code <= 0xFF) {
        bytes.push(code)
      } else if (code in CP1252_TO_BYTE) {
        bytes.push(CP1252_TO_BYTE[code])
      } else {
        // Character can't be a CP1252 byte — not mojibake
        return text
      }
    }
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes))
    // Sanity check: decoded should be shorter (multi-byte → single char)
    if (decoded.length < text.length) return decoded
  } catch {
    // Decoding failed — not actually mojibake or mixed encoding
  }

  return text
}
